<template>
  <div>
    <Navbar />

    <main class="auth-page">
      <div class="auth-bg">
        <div class="auth-bg-overlay"></div>
      </div>

      <div class="auth-container">

        <!-- Left: Branding -->
        <div class="auth-brand">
          <div class="brand-content">
            <div class="brand-badge">
              <font-awesome-icon icon="star" class="me-1" /> Free to Get Started
            </div>
            <h2 class="brand-title">Run your entire business from one powerful platform.</h2>
            <p class="brand-sub">
              Join businesses already using Mapiibs to manage inventory, automate payroll, generate invoices, track HR, and gain complete financial visibility — all in one place.
            </p>
            <ul class="brand-perks">
              <li><font-awesome-icon icon="check-circle" class="perk-icon" /> 13 integrated modules — one subscription</li>
              <li><font-awesome-icon icon="check-circle" class="perk-icon" /> No credit card required to start</li>
              <li><font-awesome-icon icon="check-circle" class="perk-icon" /> Multi-user, multi-location &amp; multi-currency</li>
              <li><font-awesome-icon icon="check-circle" class="perk-icon" /> Enterprise-grade security &amp; role management</li>
              <li><font-awesome-icon icon="check-circle" class="perk-icon" /> Cancel or upgrade any time</li>
            </ul>
            <div class="brand-modules">
              <span class="brand-mod"><font-awesome-icon icon="boxes" class="bm-icon" /> Inventory</span>
              <span class="brand-mod"><font-awesome-icon icon="calculator" class="bm-icon" /> Accounting</span>
              <span class="brand-mod"><font-awesome-icon icon="coins" class="bm-icon" /> Payroll</span>
              <span class="brand-mod"><font-awesome-icon icon="users" class="bm-icon" /> HR</span>
              <span class="brand-mod"><font-awesome-icon icon="file-invoice" class="bm-icon" /> Invoicing</span>
              <span class="brand-mod"><font-awesome-icon icon="hand-holding-usd" class="bm-icon" /> Loans</span>
              <span class="brand-mod"><font-awesome-icon icon="address-card" class="bm-icon" /> Debtors</span>
              <span class="brand-mod"><font-awesome-icon icon="chart-bar" class="bm-icon" /> Reports</span>
            </div>
          </div>
        </div>

        <!-- Right: Form card -->
        <div class="auth-card-wrap">
          <div class="auth-card">

            <div class="auth-card-header">
              <h3 class="auth-title">Create your account</h3>
              <p class="auth-sub">Already have one? <router-link to="/auth/login" class="auth-link">Sign in</router-link></p>
            </div>

            <!-- Validation errors -->
            <div v-if="hasErrors" class="error-box">
              <div class="error-box-title">
                <font-awesome-icon icon="exclamation-circle" class="me-1" /> Please fix the following:
              </div>
              <ul class="error-list">
                <li v-for="(err, i) in flatErrors" :key="i">{{ err }}</li>
              </ul>
            </div>

            <form @submit.prevent="registerUser" novalidate>

              <!-- Name row -->
              <div class="form-row-2">
                <div class="form-group-styled">
                  <label class="form-label-styled">First Name <span class="req">*</span></label>
                  <input
                    v-model="first_name"
                    type="text"
                    class="form-input-styled"
                    :class="{ 'input--error': fieldError('first_name') }"
                    placeholder="John"
                    required
                  />
                  <p v-if="fieldError('first_name')" class="field-error">{{ fieldError('first_name') }}</p>
                </div>
                <div class="form-group-styled">
                  <label class="form-label-styled">Last Name <span class="req">*</span></label>
                  <input
                    v-model="last_name"
                    type="text"
                    class="form-input-styled"
                    :class="{ 'input--error': fieldError('last_name') }"
                    placeholder="Doe"
                    required
                  />
                  <p v-if="fieldError('last_name')" class="field-error">{{ fieldError('last_name') }}</p>
                </div>
              </div>

              <div class="form-group-styled">
                <label class="form-label-styled">Company Name <span class="req">*</span></label>
                <input
                  v-model="company_name"
                  type="text"
                  class="form-input-styled"
                  :class="{ 'input--error': fieldError('company_name') }"
                  placeholder="Acme Ltd."
                  required
                />
                <p v-if="fieldError('company_name')" class="field-error">{{ fieldError('company_name') }}</p>
              </div>

              <div class="form-group-styled">
                <label class="form-label-styled">Email Address <span class="req">*</span></label>
                <input
                  v-model="email"
                  type="email"
                  class="form-input-styled"
                  :class="{ 'input--error': fieldError('email') }"
                  placeholder="you@company.com"
                  required
                />
                <p v-if="fieldError('email')" class="field-error">{{ fieldError('email') }}</p>
              </div>

              <div class="form-group-styled">
                <label class="form-label-styled">Phone Number <span class="req">*</span></label>
                <vue-tel-input
                  :value="phone"
                  @input="onInput"
                  @validate="onPhoneValidate"
                  class="tel-input"
                  :class="{ 'input--error': fieldError('phone_no') || phoneInvalid }"
                />
                <p v-if="phoneInvalid" class="field-error">Please enter a valid phone number.</p>
                <p v-else-if="fieldError('phone_no')" class="field-error">{{ fieldError('phone_no') }}</p>
              </div>

              <!-- Password row -->
              <div class="form-row-2">
                <div class="form-group-styled">
                  <label class="form-label-styled">Password <span class="req">*</span></label>
                  <div class="pw-wrap">
                    <input
                      v-model="password"
                      :type="showPassword ? 'text' : 'password'"
                      class="form-input-styled"
                      :class="{ 'input--error': fieldError('password') }"
                      placeholder="Min. 8 characters"
                      required
                    />
                    <button type="button" class="pw-toggle" @click="showPassword = !showPassword" tabindex="-1">
                      <font-awesome-icon :icon="showPassword ? 'eye-slash' : 'eye'" />
                    </button>
                  </div>
                  <!-- Password strength checklist -->
                  <div v-if="password.length > 0" class="pw-rules">
                    <span :class="['pw-rule', pwRules.length ? 'pw-rule--ok' : 'pw-rule--bad']">
                      <font-awesome-icon :icon="pwRules.length ? 'check' : 'times'" /> At least 8 characters
                    </span>
                    <span :class="['pw-rule', pwRules.upper ? 'pw-rule--ok' : 'pw-rule--bad']">
                      <font-awesome-icon :icon="pwRules.upper ? 'check' : 'times'" /> Uppercase letter
                    </span>
                    <span :class="['pw-rule', pwRules.lower ? 'pw-rule--ok' : 'pw-rule--bad']">
                      <font-awesome-icon :icon="pwRules.lower ? 'check' : 'times'" /> Lowercase letter
                    </span>
                    <span :class="['pw-rule', pwRules.number ? 'pw-rule--ok' : 'pw-rule--bad']">
                      <font-awesome-icon :icon="pwRules.number ? 'check' : 'times'" /> Number
                    </span>
                    <span :class="['pw-rule', pwRules.special ? 'pw-rule--ok' : 'pw-rule--bad']">
                      <font-awesome-icon :icon="pwRules.special ? 'check' : 'times'" /> Special character
                    </span>
                  </div>
                  <p v-if="fieldError('password')" class="field-error">{{ fieldError('password') }}</p>
                </div>

                <div class="form-group-styled">
                  <label class="form-label-styled">Confirm Password <span class="req">*</span></label>
                  <div class="pw-wrap">
                    <input
                      v-model="password_confirmation"
                      :type="showConfirmPassword ? 'text' : 'password'"
                      class="form-input-styled"
                      :class="{ 'input--error': fieldError('password_confirmation'), 'input--warning': passwordMismatch }"
                      placeholder="Repeat password"
                      required
                    />
                    <button type="button" class="pw-toggle" @click="showConfirmPassword = !showConfirmPassword" tabindex="-1">
                      <font-awesome-icon :icon="showConfirmPassword ? 'eye-slash' : 'eye'" />
                    </button>
                  </div>
                  <p v-if="passwordMismatch" class="field-error">Passwords do not match.</p>
                </div>
              </div>

              <div class="form-group-styled">
                <label class="form-label-styled">Referral Code <span class="optional">(optional)</span></label>
                <input
                  v-model="referred_by"
                  type="text"
                  class="form-input-styled"
                  placeholder="Enter referral code if you have one"
                />
              </div>

              <!-- Terms -->
              <label class="terms-check">
                <input type="checkbox" v-model="agreedToTerms" required />
                <span class="terms-text">
                  I agree to the
                  <a href="/terms-and-conditions" target="_blank" rel="noopener" class="auth-link" @click.stop>
                    Terms and Conditions
                  </a>
                </span>
              </label>

              <!-- Submit -->
              <button
                type="submit"
                class="submit-btn"
                :disabled="loading || !agreedToTerms || passwordMismatch || !passwordStrong || phoneInvalid"
              >
                <font-awesome-icon v-if="loading" icon="spinner" spin class="me-1" />
                {{ loading ? 'Creating account...' : 'Create Account' }}
              </button>

            </form>

            <div class="auth-card-footer">
              <p>Already have an account? <router-link to="/auth/login" class="auth-link">Sign in here</router-link></p>
            </div>

          </div>
        </div>

      </div>
    </main>

    <Footer />
  </div>
</template>

<script>
import { ref, computed } from 'vue';
import axios from 'axios';
import { useToast } from 'vue-toastification';
import { useRouter } from 'vue-router';
import Navbar from '../components/Navbar';
import Footer from '../components/footer';

export default {
  name: 'RegisterPage',

  components: { Navbar, Footer },

  setup() {
    const toast  = useToast();
    const router = useRouter();

    // ── Fields ───────────────────────────────────────────────────────
    const first_name            = ref('');
    const last_name             = ref('');
    const company_name          = ref('');
    const email                 = ref('');
    const phone                 = ref('');
    const password              = ref('');
    const password_confirmation = ref('');
    const referred_by           = ref('');
    const agreedToTerms         = ref(false);

    // ── UI ───────────────────────────────────────────────────────────
    const loading              = ref(false);
    const showPassword         = ref(false);
    const showConfirmPassword  = ref(false);
    const validationErrors     = ref({});
    const phoneValid           = ref(null); // null = not yet validated

    const normaliseErrors = (msg) => {
      if (!msg) return {};
      if (typeof msg === 'string')  return { general: [msg] };
      if (Array.isArray(msg))       return { general: msg.flat() };
      return msg;
    };

    // ── Computed ─────────────────────────────────────────────────────
    const passwordMismatch = computed(() =>
      password_confirmation.value.length > 0 &&
      password.value !== password_confirmation.value
    );

    const pwRules = computed(() => ({
      length:  password.value.length >= 8,
      upper:   /[A-Z]/.test(password.value),
      lower:   /[a-z]/.test(password.value),
      number:  /[0-9]/.test(password.value),
      special: /[^A-Za-z0-9]/.test(password.value),
    }));

    const passwordStrong = computed(() => Object.values(pwRules.value).every(Boolean));

    // Show phone error only after the user has interacted with the field
    const phoneInvalid = computed(() => phoneValid.value === false);

    const hasErrors  = computed(() => Object.keys(validationErrors.value).length > 0);
    const flatErrors = computed(() => Object.values(validationErrors.value).flat());
    const fieldError = (field) => {
      const val = validationErrors.value;
      if (!val) return null;
      if (typeof val === 'object' && !Array.isArray(val)) return val[field]?.[0] ?? null;
      return null;
    };

    // ── Phone ────────────────────────────────────────────────────────
    const onInput = (_, phoneObject) => {
      if (phoneObject?.number) phone.value = phoneObject.number;
    };

    const onPhoneValidate = (phoneObject) => {
      if (phoneObject?.number) {
        phoneValid.value = phoneObject.valid === true;
      }
    };

    // ── Submit ────────────────────────────────────────────────────────
    const registerUser = async () => {
      if (passwordMismatch.value) return;
      if (!passwordStrong.value) {
        validationErrors.value = { password: ['Password does not meet the required strength.'] };
        return;
      }
      if (phoneValid.value === false) return;
      loading.value = true;
      validationErrors.value = {};

      try {
        const { data } = await axios.post('/register-web', {
          first_name:            first_name.value,
          last_name:             last_name.value,
          company_name:          company_name.value,
          email:                 email.value,
          phone_no:              phone.value,
          password:              password.value,
          password_confirmation: password_confirmation.value,
          referred_by:           referred_by.value,
        });
        toast.success(data.message);
        router.push('/auth/login');
      } catch (err) {
        const status  = err.response?.status;
        const message = err.response?.data?.message;
        validationErrors.value = normaliseErrors(message);
        toast.error(status === 422 ? 'Please fix the errors below.' : (message ?? 'An error occurred. Please try again.'));
      } finally {
        loading.value = false;
      }
    };

    return {
      first_name, last_name, company_name, email, phone,
      password, password_confirmation, referred_by, agreedToTerms,
      loading, showPassword, showConfirmPassword,
      validationErrors, hasErrors, flatErrors, fieldError,
      passwordMismatch, passwordStrong, pwRules, phoneInvalid,
      onInput, onPhoneValidate, registerUser,
    };
  },
};
</script>

<style scoped>
/* ── Page shell ──────────────────────────────────────────────────────── */
.auth-page {
  min-height: 100vh;
  position: relative;
  display: flex;
  align-items: stretch;
}

.auth-bg {
  position: fixed;
  inset: 0;
  z-index: 0;
  background: url('/static/img/banner-bg-5.jpg') center/cover no-repeat;
}
.auth-bg-overlay {
  position: absolute;
  inset: 0;
  background: linear-gradient(135deg, rgba(5,27,53,.95) 0%, rgba(0,74,173,.8) 100%);
}

.auth-container {
  position: relative;
  z-index: 1;
  display: grid;
  grid-template-columns: 1fr 1fr;
  width: 100%;
  max-width: 1080px;
  margin: 0 auto;
  padding: 40px 24px;
  gap: 40px;
  align-items: center;
  min-height: 100vh;
}

/* ── Brand side ──────────────────────────────────────────────────────── */
.auth-brand   { display:flex; align-items:center; padding:20px 0; }
.brand-content { display:flex; flex-direction:column; gap:20px; }
.brand-title  { font-size:clamp(24px,3.5vw,36px); font-weight:800; color:#fff; line-height:1.25; margin:0; }
.brand-sub    { font-size:15px; color:rgba(255,255,255,.7); line-height:1.7; margin:0; }
.brand-perks  { list-style:none; padding:0; margin:0; display:flex; flex-direction:column; gap:12px; }
.brand-perks li { display:flex; align-items:center; gap:10px; font-size:14px; font-weight:500; color:rgba(255,255,255,.85); }
.perk-icon    { color:#4cde80; font-size:15px; flex-shrink:0; }

.brand-badge {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  background: rgba(255,255,255,.1);
  border: 1px solid rgba(255,255,255,.18);
  color: rgba(255,255,255,.9);
  border-radius: 20px;
  padding: 4px 14px;
  font-size: 12px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: .07em;
  width: fit-content;
}
.brand-modules {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 4px;
}
.brand-mod {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  background: rgba(255,255,255,.08);
  border: 1px solid rgba(255,255,255,.14);
  color: rgba(255,255,255,.72);
  border-radius: 6px;
  padding: 4px 10px;
  font-size: 11.5px;
  font-weight: 600;
  letter-spacing: .02em;
}
.bm-icon { font-size:10px; color:rgba(255,255,255,.5); }

/* ── Card side ───────────────────────────────────────────────────────── */
.auth-card-wrap { display:flex; align-items:flex-start; padding:20px 0; }
.auth-card {
  background: #fff;
  border-radius: 16px;
  padding: 36px 32px;
  width: 100%;
  box-shadow: 0 24px 60px rgba(0,0,0,.2);
}
.auth-card-header { margin-bottom:24px; }
.auth-title  { font-size:22px; font-weight:800; color:#1a2b45; margin:0 0 6px; }
.auth-sub    { font-size:13.5px; color:#7a90b0; margin:0; }
.auth-link   { color:#004AAD; font-weight:700; text-decoration:none; }
.auth-link:hover { text-decoration:underline; }

/* ── Error box ───────────────────────────────────────────────────────── */
.error-box       { background:#fff5f5; border-left:3px solid #e03535; border-radius:0 8px 8px 0; padding:12px 14px; margin-bottom:18px; }
.error-box-title { font-size:13px; font-weight:700; color:#e03535; margin-bottom:6px; display:flex; align-items:center; }
.error-list      { margin:0; padding-left:16px; }
.error-list li   { font-size:12.5px; color:#c02828; margin-bottom:3px; }
.me-1 { margin-right:4px; }

/* ── Form ────────────────────────────────────────────────────────────── */
.form-row-2 { display:grid; grid-template-columns:1fr 1fr; gap:14px; }
.form-group-styled { display:flex; flex-direction:column; gap:5px; margin-bottom:14px; }
.form-label-styled { font-size:13px; font-weight:600; color:#1a2b45; }
.req      { color:#e03535; }
.optional { font-size:11px; font-weight:400; color:#7a90b0; }

.form-input-styled {
  width: 100%;
  padding: 9px 13px;
  border: 1px solid #dde5f0;
  border-radius: 8px;
  font-size: 13.5px;
  color: #1a2b45;
  background: #fff;
  outline: none;
  transition: border-color .15s, box-shadow .15s;
  font-family: inherit;
}
.form-input-styled:focus { border-color:#004AAD; box-shadow:0 0 0 3px rgba(0,74,173,.08); }
.input--error   { border-color:#e03535 !important; }
.input--warning { border-color:#e08c00 !important; }
.field-error    { font-size:12px; color:#e03535; margin:2px 0 0; }

/* ── Tel input ───────────────────────────────────────────────────────── */
.tel-input { border:1px solid #dde5f0; border-radius:8px; overflow:hidden; }
.tel-input.input--error { border-color:#e03535; }

/* ── Password wrap ───────────────────────────────────────────────────── */
.pw-wrap   { position:relative; display:flex; align-items:center; }
.pw-wrap .form-input-styled { padding-right:40px; }
.pw-toggle { position:absolute; right:10px; border:none; background:transparent; color:#7a90b0; cursor:pointer; font-size:14px; padding:0; transition:color .15s; }
.pw-toggle:hover { color:#1a2b45; }

/* ── Password rules ──────────────────────────────────────────────────── */
.pw-rules {
  display: flex;
  flex-wrap: wrap;
  gap: 5px 10px;
  margin-top: 6px;
}
.pw-rule {
  font-size: 11.5px;
  font-weight: 500;
  display: flex;
  align-items: center;
  gap: 4px;
}
.pw-rule--ok  { color: #16a34a; }
.pw-rule--bad { color: #94a3b8; }

/* ── Terms ───────────────────────────────────────────────────────────── */
.terms-check { display:flex; align-items:flex-start; gap:10px; cursor:pointer; margin-bottom:20px; }
.terms-check input[type=checkbox] { width:16px; height:16px; margin-top:1px; flex-shrink:0; accent-color:#004AAD; cursor:pointer; }
.terms-text { font-size:13px; color:#4a5568; line-height:1.5; }

/* ── Submit ──────────────────────────────────────────────────────────── */
.submit-btn {
  width: 100%;
  padding: 12px;
  background: #004AAD;
  color: #fff;
  border: none;
  border-radius: 9px;
  font-size: 15px;
  font-weight: 700;
  cursor: pointer;
  transition: background .15s, transform .1s;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
}
.submit-btn:hover:not(:disabled)  { background:#003b8e; }
.submit-btn:active:not(:disabled) { transform:scale(.98); }
.submit-btn:disabled { opacity:.5; cursor:not-allowed; }

/* ── Card footer ─────────────────────────────────────────────────────── */
.auth-card-footer { margin-top:20px; padding-top:16px; border-top:1px solid #f0f4f8; text-align:center; font-size:13px; color:#7a90b0; }

/* ── Responsive ──────────────────────────────────────────────────────── */
@media (max-width:900px) {
  .auth-container { grid-template-columns:1fr; max-width:520px; }
  .auth-brand     { display:none; }
  .auth-card-wrap { padding:0; }
}

@media (max-width:480px) {
  .auth-card    { padding:28px 20px; }
  .form-row-2   { grid-template-columns:1fr; }
  .auth-container { padding:24px 16px; }
}
</style>